import { toast } from "@/lib/instrumented-toast";
import { messageFromAxiosOrError } from "@/lib/invite-link";
import { queryKeys } from "@/lib/query-keys";
import { formatFileSize } from "@/lib/utils";
import { useApiInstance } from "@/providers/api-provider";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { zipSync } from "fflate";

export type FileShareConfig = {
  enabled: boolean;
  max_upload_bytes: number;
  retention_days: number;
};

export type SharedFile = {
  id: string;
  filename: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
  expires_at: string;
  can_delete: boolean;
};

export type SharedFileList = {
  files: SharedFile[];
  total: number;
  can_upload: boolean;
  can_view_activity: boolean;
};

export type SharedFileEvent = {
  id: string;
  action: "upload" | "download" | "delete";
  user_email: string;
  file_id: string;
  filename: string;
  size_bytes: number | null;
  created_at: string;
};

export type SharedFileActivity = {
  events: SharedFileEvent[];
  total: number;
};

export type SharedFilesSortBy = "filename" | "size_bytes" | "uploaded_at" | "expires_at";

export type SharedFilesQuery = { page: number; pageSize: number; sortBy: SharedFilesSortBy; sortDir: "asc" | "desc" };

type UploadUrlResponse = {
  url: string;
  fields: Record<string, string>;
};

export function useFileShareConfig() {
  return useQuery({
    queryKey: queryKeys.fileShareConfig,
    queryFn: async (): Promise<FileShareConfig> => {
      const response = await useApiInstance.get<FileShareConfig>("/api/v1/file-share/config");
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSharedFiles(orgId: string | undefined, query: SharedFilesQuery) {
  return useQuery({
    queryKey: queryKeys.sharedFiles(orgId ?? "", query),
    queryFn: async (): Promise<SharedFileList> => {
      const response = await useApiInstance.get<SharedFileList>(`/api/v1/file-share/${orgId}/files`, {
        params: { page: query.page, page_size: query.pageSize, sort_by: query.sortBy, sort_dir: query.sortDir },
      });
      return response.data;
    },
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });
}

export type SharedFileActivitySortBy = "created_at" | "user_email" | "action" | "filename" | "size_bytes";

export type SharedFileActivityQuery = {
  page: number;
  pageSize: number;
  sortBy: SharedFileActivitySortBy;
  sortDir: "asc" | "desc";
};

export function useSharedFileActivity(orgId: string | undefined, query: SharedFileActivityQuery) {
  return useQuery({
    queryKey: queryKeys.sharedFileActivity(orgId ?? "", query),
    queryFn: async (): Promise<SharedFileActivity> => {
      const response = await useApiInstance.get<SharedFileActivity>(`/api/v1/file-share/${orgId}/activity`, {
        params: { page: query.page, page_size: query.pageSize, sort_by: query.sortBy, sort_dir: query.sortDir },
      });
      return response.data;
    },
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });
}

export type TransferProgress = {
  index: number;
  total: number;
  percent: number;
};

async function uploadOne(orgId: string, file: File, onPercent: (percent: number) => void) {
  const presign = await useApiInstance.post<UploadUrlResponse>(`/api/v1/file-share/${orgId}/files/upload-url`, {
    filename: file.name,
    size_bytes: file.size,
    content_type: file.type || undefined,
  });
  const formData = new FormData();
  Object.entries(presign.data.fields).forEach(([key, value]) => formData.append(key, value));
  formData.append("file", file);
  await axios.post(presign.data.url, formData, {
    onUploadProgress: (event) => onPercent(Math.round((event.loaded / (event.total ?? file.size)) * 100)),
  });
}

export function useUploadSharedFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      files,
      onProgress,
      onFileUploaded,
    }: {
      orgId: string;
      files: File[];
      onProgress: (progress: TransferProgress) => void;
      onFileUploaded: (file: File) => void;
    }) => {
      for (const [index, file] of files.entries()) {
        try {
          await uploadOne(orgId, file, (percent) => onProgress({ index, total: files.length, percent }));
        } catch (error) {
          throw new Error(`${file.name}: ${messageFromAxiosOrError(error)}`);
        }
        onFileUploaded(file);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sharedFiles(variables.orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.sharedFileActivity(variables.orgId) });
    },
    onSuccess: (_data, variables) => {
      toast.success(
        variables.files.length === 1
          ? `Uploaded ${variables.files[0].name}`
          : `Uploaded ${variables.files.length} files`,
      );
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });
}

/** Browsers save the presigned response (Content-Disposition: attachment) instead of navigating. */
function clickDownload(href: string, downloadName?: string) {
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.rel = "noopener";
  if (downloadName) anchor.download = downloadName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function uniqueEntryName(name: string, taken: Set<string>): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let candidate = name;
  for (let n = 2; taken.has(candidate); n++) candidate = `${stem} (${n})${ext}`;
  taken.add(candidate);
  return candidate;
}

/** The archive is assembled in browser memory, so the selection is capped well below what a tab can hold. */
export const MAX_ARCHIVE_BYTES = 1024 ** 3;

type DownloadTarget = Pick<SharedFile, "id" | "filename" | "size_bytes">;

export function useDownloadSharedFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      files,
      onProgress,
    }: {
      orgId: string;
      files: DownloadTarget[];
      onProgress: (progress: TransferProgress) => void;
    }) => {
      const presign = async (file: DownloadTarget): Promise<string> => {
        try {
          const response = await useApiInstance.get<{ url: string }>(
            `/api/v1/file-share/${orgId}/files/${file.id}/download-url`,
          );
          return response.data.url;
        } catch (error) {
          throw new Error(`${file.filename}: ${messageFromAxiosOrError(error)}`);
        }
      };

      if (files.length === 1) {
        clickDownload(await presign(files[0]));
        return;
      }

      const totalBytes = files.reduce((sum, file) => sum + file.size_bytes, 0);
      if (totalBytes > MAX_ARCHIVE_BYTES) {
        throw new Error(
          `the selected files total ${formatFileSize(totalBytes)}; archives are limited to ${formatFileSize(MAX_ARCHIVE_BYTES)}. Deselect some files or download them one at a time.`,
        );
      }
      const entries: Record<string, Uint8Array> = {};
      const taken = new Set<string>();
      for (const [index, file] of files.entries()) {
        onProgress({ index, total: files.length, percent: 0 });
        const response = await fetch(await presign(file));
        if (!response.ok) throw new Error(`${file.filename}: storage returned ${response.status}`);
        entries[uniqueEntryName(file.filename, taken)] = new Uint8Array(await response.arrayBuffer());
      }
      /** Stored, not deflated: shared files are usually compressed already, so this stays sub-second. */
      const zipped = zipSync(entries, { level: 0 });
      const blobUrl = URL.createObjectURL(new Blob([zipped as BlobPart], { type: "application/zip" }));
      clickDownload(blobUrl, `shared-files-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.zip`);
      /** Revoking right away can abort the download the click just started. */
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sharedFileActivity(variables.orgId) });
    },
    onSuccess: (_data, variables) => {
      toast.success(
        variables.files.length === 1 ? "Download started" : `Downloading ${variables.files.length} files as an archive`,
      );
    },
    onError: (error) => {
      toast.error(`Download failed: ${error.message}`);
    },
  });
}

export function useDeleteSharedFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, files }: { orgId: string; files: Pick<SharedFile, "id" | "filename">[] }) => {
      for (const file of files) {
        try {
          await useApiInstance.delete(`/api/v1/file-share/${orgId}/files/${file.id}`);
        } catch (error) {
          throw new Error(`${file.filename}: ${messageFromAxiosOrError(error)}`);
        }
      }
    },
    onSuccess: (_data, variables) => {
      toast.success(variables.files.length === 1 ? "File deleted" : `Deleted ${variables.files.length} files`);
    },
    onError: (error) => {
      toast.error(`Delete failed: ${messageFromAxiosOrError(error)}`);
    },
    onSettled: (_data, _error, variables) => {
      // Files before a failure are already gone, so refresh either way.
      queryClient.invalidateQueries({ queryKey: queryKeys.sharedFiles(variables.orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.sharedFileActivity(variables.orgId) });
    },
  });
}
